/**
 * Individual API route templates for standard setup
 */

export function getTasksRouteTemplate(): string {
  return `/**
 * Vibeship DevTools Tasks API Route
 * Generated by: npx vibeship init --level standard
 */

import { NextRequest, NextResponse } from 'next/server';
import { scanForTasks } from '@vibeship/devtools/server';

export async function GET(request: NextRequest) {
  try {
    const url = new URL(request.url);
    const includeMetadata = url.searchParams.get('metadata') === 'true';
    const fileFilter = url.searchParams.get('file');
    const typeFilter = url.searchParams.get('type');
    
    const tasks = await scanForTasks({
      includeMetadata,
      fileFilter,
      typeFilter
    });
    
    return NextResponse.json({ 
      tasks,
      count: tasks.length,
      timestamp: new Date().toISOString()
    });
  } catch (error) {
    console.error('Tasks API Error:', error);
    return NextResponse.json(
      { error: 'Failed to fetch tasks' },
      { status: 500 }
    );
  }
}

export async function POST(request: NextRequest) {
  try {
    const body = await request.json();
    
    // Handle task updates (mark as done, etc.)
    if (body.action === 'update' && body.taskId) {
      // Implement task update logic
      return NextResponse.json({ 
        success: true,
        taskId: body.taskId,
        action: body.action
      });
    }
    
    return NextResponse.json(
      { error: 'Invalid request' },
      { status: 400 }
    );
  } catch (error) {
    console.error('Tasks API Error:', error);
    return NextResponse.json(
      { error: 'Failed to update task' },
      { status: 500 }
    );
  }
}
`;
}

export function getFilesRouteTemplate(): string {
  return `/**
 * Vibeship DevTools Files API Route
 * Generated by: npx vibeship init --level standard
 */

import { NextRequest, NextResponse } from 'next/server';
import { listFiles, readFile, writeFile } from '@vibeship/devtools/server';

export async function GET(request: NextRequest) {
  try {
    const url = new URL(request.url);
    const path = url.searchParams.get('path');
    
    // Get file content
    if (path) {
      const content = await readFile(path);
      return NextResponse.json({ 
        content,
        path,
        timestamp: new Date().toISOString()
      });
    }
    
    // List files
    const pattern = url.searchParams.get('pattern') || '**/*.{md,mdx}';
    const files = await listFiles(pattern);
    
    return NextResponse.json({ 
      files,
      count: files.length
    });
  } catch (error) {
    console.error('Files API Error:', error);
    return NextResponse.json(
      { error: 'Failed to process file request' },
      { status: 500 }
    );
  }
}

export async function POST(request: NextRequest) {
  try {
    // Check if file writes are disabled
    if (process.env.VIBESHIP_DISABLE_FILE_WRITES === 'true') {
      return NextResponse.json(
        { error: 'File writes are disabled in this environment' },
        { status: 403 }
      );
    }
    
    const { path, content } = await request.json();
    
    if (!path || content === undefined) {
      return NextResponse.json(
        { error: 'Path and content are required' },
        { status: 400 }
      );
    }
    
    await writeFile(path, content);
    
    return NextResponse.json({ 
      success: true,
      path,
      timestamp: new Date().toISOString()
    });
  } catch (error) {
    console.error('Files API Error:', error);
    return NextResponse.json(
      { error: 'Failed to write file' },
      { status: 500 }
    );
  }
}
`;
}

export function getAIChatRouteTemplate(): string {
  return `/**
 * Vibeship DevTools AI Chat API Route
 * Generated by: npx vibeship init --level standard
 */

import { NextRequest, NextResponse } from 'next/server';

export async function POST(request: NextRequest) {
  try {
    const { messages, model, provider } = await request.json();
    
    if (!messages || !Array.isArray(messages)) {
      return NextResponse.json(
        { error: 'Messages array is required' },
        { status: 400 }
      );
    }
    
    // Check for API keys
    const apiKey = process.env[\`\${provider?.toUpperCase() || 'OPENAI'}_API_KEY\`];
    
    if (!apiKey) {
      return NextResponse.json({
        error: 'AI provider not configured',
        message: 'Please add your AI provider API key to .env.local',
        providers: ['OPENAI_API_KEY', 'ANTHROPIC_API_KEY', 'GOOGLE_AI_API_KEY']
      }, { status: 501 });
    }
    
    // TODO: Implement actual AI provider integration
    // For now, return a placeholder response
    return NextResponse.json({
      response: 'AI chat functionality requires provider setup. Check the documentation for integration details.',
      model: model || 'gpt-3.5-turbo',
      provider: provider || 'openai'
    });
    
  } catch (error) {
    console.error('AI Chat API Error:', error);
    return NextResponse.json(
      { error: 'Failed to process chat request' },
      { status: 500 }
    );
  }
}

// Streaming endpoint for AI responses
export async function GET(request: NextRequest) {
  const encoder = new TextEncoder();
  
  const stream = new ReadableStream({
    start(controller) {
      // Send initial message
      controller.enqueue(
        encoder.encode('data: {"type": "info", "message": "AI streaming ready"}\\n\\n')
      );
      
      // Heartbeat
      const interval = setInterval(() => {
        controller.enqueue(encoder.encode(':heartbeat\\n\\n'));
      }, 30000);
      
      // Cleanup
      request.signal.addEventListener('abort', () => {
        clearInterval(interval);
        controller.close();
      });
    },
  });
  
  return new Response(stream, {
    headers: {
      'Content-Type': 'text/event-stream',
      'Cache-Control': 'no-cache',
      'Connection': 'keep-alive',
    },
  });
}
`;
}

export function getSSEStreamRouteTemplate(): string {
  return `/**
 * Vibeship DevTools SSE Stream API Route
 * Generated by: npx vibeship init --level standard
 */

import { NextRequest } from 'next/server';
import { watchTasks } from '@vibeship/devtools/server';

export async function GET(request: NextRequest) {
  const encoder = new TextEncoder();
  let watcher: any;
  
  const stream = new ReadableStream({
    async start(controller) {
      // Send initial tasks
      const { scanForTasks } = await import('@vibeship/devtools/server');
      const initialTasks = await scanForTasks();
      
      controller.enqueue(
        encoder.encode(\`data: \${JSON.stringify({ 
          type: 'initial', 
          tasks: initialTasks,
          timestamp: new Date().toISOString()
        })}\\n\\n\`)
      );
      
      // Watch for changes
      watcher = watchTasks((tasks) => {
        controller.enqueue(
          encoder.encode(\`data: \${JSON.stringify({ 
            type: 'update', 
            tasks,
            timestamp: new Date().toISOString()
          })}\\n\\n\`)
        );
      });
      
      // Heartbeat every 30 seconds
      const heartbeat = setInterval(() => {
        controller.enqueue(encoder.encode(':heartbeat\\n\\n'));
      }, 30000);
      
      // Cleanup on disconnect
      request.signal.addEventListener('abort', () => {
        clearInterval(heartbeat);
        if (watcher) watcher.close();
        controller.close();
      });
    },
  });
  
  return new Response(stream, {
    headers: {
      'Content-Type': 'text/event-stream',
      'Cache-Control': 'no-cache',
      'Connection': 'keep-alive',
      'X-Content-Type-Options': 'nosniff',
    },
  });
}
`;
}

// Pages Router templates
export function getPagesTasksTemplate(): string {
  return `/**
 * Vibeship DevTools Tasks API Route (Pages Router)
 * Generated by: npx vibeship init --level standard
 */

import type { NextApiRequest, NextApiResponse } from 'next';
import { scanForTasks } from '@vibeship/devtools/server';

export default async function handler(
  req: NextApiRequest,
  res: NextApiResponse
) {
  // Enable CORS
  res.setHeader('Access-Control-Allow-Origin', '*');
  res.setHeader('Access-Control-Allow-Methods', 'GET, POST, OPTIONS');
  res.setHeader('Access-Control-Allow-Headers', 'Content-Type');
  
  if (req.method === 'OPTIONS') {
    return res.status(200).end();
  }
  
  try {
    if (req.method === 'GET') {
      const { metadata, file, type } = req.query;
      
      const tasks = await scanForTasks({
        includeMetadata: metadata === 'true',
        fileFilter: file as string,
        typeFilter: type as string
      });
      
      return res.json({ 
        tasks,
        count: tasks.length,
        timestamp: new Date().toISOString()
      });
    }
    
    if (req.method === 'POST') {
      const { action, taskId } = req.body;
      
      if (action === 'update' && taskId) {
        // Implement task update logic
        return res.json({ 
          success: true,
          taskId,
          action
        });
      }
      
      return res.status(400).json({ error: 'Invalid request' });
    }
    
    return res.status(405).json({ error: 'Method not allowed' });
  } catch (error) {
    console.error('Tasks API Error:', error);
    return res.status(500).json({ error: 'Internal server error' });
  }
}
`;
}

export function getPagesFilesTemplate(): string {
  return `/**
 * Vibeship DevTools Files API Route (Pages Router)
 * Generated by: npx vibeship init --level standard
 */

import type { NextApiRequest, NextApiResponse } from 'next';
import { listFiles, readFile, writeFile } from '@vibeship/devtools/server';

export default async function handler(
  req: NextApiRequest,
  res: NextApiResponse
) {
  // Enable CORS
  res.setHeader('Access-Control-Allow-Origin', '*');
  res.setHeader('Access-Control-Allow-Methods', 'GET, POST, OPTIONS');
  res.setHeader('Access-Control-Allow-Headers', 'Content-Type');
  
  if (req.method === 'OPTIONS') {
    return res.status(200).end();
  }
  
  try {
    if (req.method === 'GET') {
      const { path, pattern } = req.query;
      
      if (path) {
        const content = await readFile(path as string);
        return res.json({ 
          content,
          path,
          timestamp: new Date().toISOString()
        });
      }
      
      const files = await listFiles((pattern as string) || '**/*.{md,mdx}');
      return res.json({ 
        files,
        count: files.length
      });
    }
    
    if (req.method === 'POST') {
      if (process.env.VIBESHIP_DISABLE_FILE_WRITES === 'true') {
        return res.status(403).json({ 
          error: 'File writes are disabled in this environment' 
        });
      }
      
      const { path, content } = req.body;
      
      if (!path || content === undefined) {
        return res.status(400).json({ 
          error: 'Path and content are required' 
        });
      }
      
      await writeFile(path, content);
      
      return res.json({ 
        success: true,
        path,
        timestamp: new Date().toISOString()
      });
    }
    
    return res.status(405).json({ error: 'Method not allowed' });
  } catch (error) {
    console.error('Files API Error:', error);
    return res.status(500).json({ error: 'Internal server error' });
  }
}
`;
}